import { useState, useEffect } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Switch,
  FormControlLabel,
  Alert,
} from '@mui/material'
import { getLocalPermission, setLocalPermission } from '../../utils/permission'

const PermissionModal = ({ open, onClose }) => {
  const [cameraEnabled, setCameraEnabled] = useState(false)
  const [locationEnabled, setLocationEnabled] = useState(false)

  const [cameraDenied, setCameraDenied] = useState(false)
  const [locationDenied, setLocationDenied] = useState(false)

  // ===== CAMERA =====
  const handleCameraToggle = async (e) => {
    const checked = e.target.checked

    if (checked) {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ video: true })
        stream.getTracks().forEach((t) => t.stop())

        setCameraEnabled(true)
        setCameraDenied(false)
        setLocalPermission({
          ...getLocalPermission(),
          camera: true,
        })
      } catch (err) {
        console.error('Camera permission denied', err)

        setCameraEnabled(false)
        setCameraDenied(true)
      }
    } else {
      setCameraEnabled(false)
    }
  }

  // ===== LOCATION =====
  const handleLocationToggle = async (e) => {
    const checked = e.target.checked

    if (checked) {
      navigator.geolocation.getCurrentPosition(
        () => {
          setLocationEnabled(true)
          setLocationDenied(false)
          setLocalPermission({
            ...getLocalPermission(),
            location: true,
          })
        },
        (err) => {
          console.error('Location permission denied', err)

          setLocationEnabled(false)
          setLocationDenied(true)
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
        },
      )
    } else {
      setLocationEnabled(false)
    }
  }

  const allGranted = cameraEnabled && locationEnabled

  // auto close kalau sudah aman
  useEffect(() => {
    if (allGranted) {
      onClose?.()
    }
  }, [allGranted, onClose])

  useEffect(() => {
    if (!open) return

    // ⚡ 1. ambil dari localStorage (cepat)
    const local = getLocalPermission()

    if (local.camera !== undefined) setCameraEnabled(local.camera)
    if (local.location !== undefined) setLocationEnabled(local.location)

    // 🔍 2. validasi real (async)
    const checkPermissions = async () => {
      let cam = false
      let loc = false

      try {
        const stream = await navigator.mediaDevices.getUserMedia({ video: true })
        stream.getTracks().forEach((t) => t.stop())
        cam = true
      } catch {
        cam = false
      }

      await new Promise((resolve) => {
        navigator.geolocation.getCurrentPosition(
          () => {
            loc = true
            resolve()
          },
          () => {
            loc = false
            resolve()
          },
        )
      })

      setCameraEnabled(cam)
      setLocationEnabled(loc)

      // 🔥 sync ke localStorage
      setLocalPermission({ camera: cam, location: loc })
    }

    checkPermissions()
  }, [open])

  const handleClose = (event, reason) => {
    if (reason && reason === 'backdropClick') return
    onClose?.()
  }

  return (
    <Dialog open={open} onClose={handleClose} disableEscapeKeyDown fullWidth maxWidth="xs">
      <DialogTitle>Aktivasi Perizinan</DialogTitle>

      <DialogContent>
        <Box display="flex" flexDirection="column" gap={2} mt={1}>
          {/* INFO GLOBAL */}
          {(cameraDenied || locationDenied) && (
            <Alert severity="warning">
              Jika izin tidak muncul, silakan aktifkan manual di pengaturan browser.
            </Alert>
          )}

          {/* CAMERA */}
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography>Perizinan Kamera</Typography>
            <FormControlLabel
              control={<Switch checked={cameraEnabled} onChange={handleCameraToggle} />}
              label=""
            />
          </Box>

          {cameraDenied && (
            <Typography color="error" fontSize={12}>
              Izin kamera ditolak. Aktifkan di pengaturan/browser. Jika sudah reload halaman.
            </Typography>
          )}

          {/* LOCATION */}
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography>Perizinan Lokasi</Typography>
            <FormControlLabel
              control={<Switch checked={locationEnabled} onChange={handleLocationToggle} />}
              label=""
            />
          </Box>

          {locationDenied && (
            <Typography color="error" fontSize={12}>
              Izin lokasi ditolak. Aktifkan di pengaturan/browser. Jika sudah reload halaman.
            </Typography>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ justifyContent: 'space-between', px: 3, pb: 2 }}>
        <Typography fontSize={12} color="text.secondary">
          Pastikan semua izin aktif sebelum memulai tes
        </Typography>

        <Button onClick={onClose} disabled={!allGranted}>
          Lanjut
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default PermissionModal
